/**
 * Storage Retention Service
 *
 * Manages data retention policies, auto-pruning of stale data,
 * and quota protection for localStorage.
 */

import { STORAGE_KEYS, getStorageItem, setStorageItem, removeStorageItem, onQuotaExceeded } from './storageService';
import { backendKeys } from './storageBackend';

// --- Retention Policy Types ---

type RetentionPriority = 1 | 2 | 3;
type RetentionCategory = 'progress' | 'session' | 'cache' | 'preferences' | 'analytics';

interface RetentionPolicy {
  maxAgeDays: number | null; // null = never expire
  priority: RetentionPriority; // 1=critical, 2=important, 3=nice-to-have
  category: RetentionCategory;
}

// --- Retention Policies per Key ---

const RETENTION_POLICIES: Partial<Record<string, RetentionPolicy>> = {
  // Priority 1: Critical — never auto-delete
  [STORAGE_KEYS.PROGRESS]: { maxAgeDays: null, priority: 1, category: 'progress' },
  [STORAGE_KEYS.ONBOARDING]: { maxAgeDays: null, priority: 1, category: 'preferences' },
  [STORAGE_KEYS.DARK_MODE]: { maxAgeDays: null, priority: 1, category: 'preferences' },
  [STORAGE_KEYS.TTS_ENABLED]: { maxAgeDays: null, priority: 1, category: 'preferences' },
  [STORAGE_KEYS.TTS_SPEED]: { maxAgeDays: null, priority: 1, category: 'preferences' },
  [STORAGE_KEYS.TTS_AUTOPLAY]: { maxAgeDays: null, priority: 1, category: 'preferences' },
  [STORAGE_KEYS.SOUND_ENABLED]: { maxAgeDays: null, priority: 1, category: 'preferences' },
  [STORAGE_KEYS.TOPIC_PROGRESS]: { maxAgeDays: null, priority: 1, category: 'progress' },
  [STORAGE_KEYS.READING_PROGRESS]: { maxAgeDays: null, priority: 1, category: 'progress' },
  [STORAGE_KEYS.STORY_PROGRESS]: { maxAgeDays: null, priority: 1, category: 'progress' },
  [STORAGE_KEYS.PLACEMENT_TEST]: { maxAgeDays: null, priority: 1, category: 'progress' },
  [STORAGE_KEYS.NOTIFICATION_SETTINGS]: { maxAgeDays: null, priority: 1, category: 'preferences' },
  [STORAGE_KEYS.THEME]: { maxAgeDays: null, priority: 1, category: 'preferences' },
  [STORAGE_KEYS.AVATAR]: { maxAgeDays: null, priority: 1, category: 'preferences' },

  // Priority 2: Important — keep 90 days
  [STORAGE_KEYS.FLASHCARD_STATS]: { maxAgeDays: 90, priority: 2, category: 'session' },
  [STORAGE_KEYS.FLASHCARD_CONFIG]: { maxAgeDays: null, priority: 2, category: 'preferences' },
  [STORAGE_KEYS.WAZN_STATS]: { maxAgeDays: 90, priority: 2, category: 'session' },
  [STORAGE_KEYS.MORPH_STATS]: { maxAgeDays: 90, priority: 2, category: 'session' },
  [STORAGE_KEYS.TYPING_STATS]: { maxAgeDays: 90, priority: 2, category: 'session' },
  [STORAGE_KEYS.STUDY_TIME]: { maxAgeDays: 90, priority: 2, category: 'session' },
  [STORAGE_KEYS.CHALLENGES]: { maxAgeDays: 90, priority: 2, category: 'session' },
  [STORAGE_KEYS.STRAND_BALANCE]: { maxAgeDays: 90, priority: 2, category: 'session' },
  [STORAGE_KEYS.LISTENING_PROGRESS]: { maxAgeDays: 90, priority: 2, category: 'progress' },
  [STORAGE_KEYS.DIALOGUE_PROGRESS]: { maxAgeDays: 90, priority: 2, category: 'progress' },
  [STORAGE_KEYS.FRIEND_RIVALRIES]: { maxAgeDays: 90, priority: 2, category: 'session' },
  [STORAGE_KEYS.COMPANION_RELATIONSHIPS]: { maxAgeDays: null, priority: 2, category: 'progress' },

  // Priority 3: Nice-to-have — keep 30 days
  [STORAGE_KEYS.SESSION_HISTORY]: { maxAgeDays: 30, priority: 3, category: 'analytics' },
  [STORAGE_KEYS.SMART_REVIEW_STATS]: { maxAgeDays: 30, priority: 3, category: 'cache' },
  [STORAGE_KEYS.DAILY_PUZZLE]: { maxAgeDays: 30, priority: 3, category: 'cache' },
  [STORAGE_KEYS.GRAMMAR_RECENT]: { maxAgeDays: 30, priority: 3, category: 'cache' },
  [STORAGE_KEYS.NOTIFICATION_DISMISSED]: { maxAgeDays: 30, priority: 3, category: 'cache' },
  [STORAGE_KEYS.FEATURE_TOUR_COMPLETED]: { maxAgeDays: null, priority: 3, category: 'preferences' },
  [STORAGE_KEYS.COMPANION_SHOWN]: { maxAgeDays: 30, priority: 3, category: 'cache' },
  [STORAGE_KEYS.MISTAKE_PATTERNS]: { maxAgeDays: 30, priority: 3, category: 'analytics' },
  [STORAGE_KEYS.DYNAMIC_DIFFICULTY]: { maxAgeDays: 30, priority: 3, category: 'cache' },
  [STORAGE_KEYS.INTERLEAVING_STATS]: { maxAgeDays: 30, priority: 3, category: 'analytics' },
  [STORAGE_KEYS.SYNC_QUEUE]: { maxAgeDays: 7, priority: 3, category: 'cache' },
  [STORAGE_KEYS.SYNC_STATE]: { maxAgeDays: 7, priority: 3, category: 'cache' },
  [STORAGE_KEYS.SEASONAL_EVENTS]: { maxAgeDays: 30, priority: 3, category: 'cache' },
};

// Default policy for keys not explicitly listed
const DEFAULT_POLICY: RetentionPolicy = { maxAgeDays: 60, priority: 3, category: 'cache' };

// Metadata key: stores last-written timestamps per storage key
const RETENTION_META_KEY = 'madina_retention_meta';

// Conservative localStorage quota estimate (bytes)
const ESTIMATED_QUOTA = 5 * 1024 * 1024; // 5 MB

// --- Internal Helpers ---

interface RetentionMeta {
  [storageKey: string]: number; // timestamp of last write
}

function loadMeta(): RetentionMeta {
  return getStorageItem<RetentionMeta>(RETENTION_META_KEY, {});
}

function saveMeta(meta: RetentionMeta): void {
  setStorageItem(RETENTION_META_KEY, meta);
}

function getPolicyForKey(key: string): RetentionPolicy {
  return RETENTION_POLICIES[key] ?? DEFAULT_POLICY;
}

function getAllMadinaKeys(): string[] {
  return backendKeys().filter(k => k.startsWith('madina'));
}

// --- Public API ---

/**
 * Record that a key was written (call after setStorageItem).
 */
export function touchKey(key: string): void {
  const meta = loadMeta();
  meta[key] = Date.now();
  saveMeta(meta);
}

/**
 * Estimate total localStorage bytes used by the app.
 */
export function getStorageUsage(): number {
  if (typeof window === 'undefined') return 0;
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    const value = localStorage.getItem(key);
    if (value) {
      // Each char is ~2 bytes in UTF-16
      total += (key.length + value.length) * 2;
    }
  }
  return total;
}

/**
 * Get quota usage as a percentage (0-100).
 */
export function getQuotaPercent(): number {
  const usage = getStorageUsage();
  return Math.round((usage / ESTIMATED_QUOTA) * 100);
}

/**
 * Get per-category storage breakdown.
 */
export function getStorageReport(): Record<RetentionCategory | 'other', { keys: number; bytes: number }> {
  const report: Record<string, { keys: number; bytes: number }> = {
    progress: { keys: 0, bytes: 0 },
    session: { keys: 0, bytes: 0 },
    cache: { keys: 0, bytes: 0 },
    preferences: { keys: 0, bytes: 0 },
    analytics: { keys: 0, bytes: 0 },
    other: { keys: 0, bytes: 0 },
  };

  if (typeof window === 'undefined') return report;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    const value = localStorage.getItem(key) ?? '';
    const bytes = (key.length + value.length) * 2;
    const policy = RETENTION_POLICIES[key];
    const cat = policy?.category ?? 'other';
    report[cat].keys++;
    report[cat].bytes += bytes;
  }

  return report;
}

/**
 * Delete storage keys that have exceeded their retention period.
 * Never deletes priority-1 keys.
 */
export function pruneExpiredData(): { pruned: number; keys: string[] } {
  const meta = loadMeta();
  const now = Date.now();
  const pruned: string[] = [];

  for (const key of getAllMadinaKeys()) {
    const policy = getPolicyForKey(key);
    if (policy.priority === 1 || policy.maxAgeDays === null) continue;

    const lastWritten = meta[key];
    if (!lastWritten) continue; // No metadata — skip (can't determine age)

    const ageDays = (now - lastWritten) / (1000 * 60 * 60 * 24);
    if (ageDays > policy.maxAgeDays) {
      removeStorageItem(key);
      delete meta[key];
      pruned.push(key);
    }
  }

  if (pruned.length > 0) saveMeta(meta);
  return { pruned: pruned.length, keys: pruned };
}

/**
 * Emergency cleanup when quota is critically high.
 * Deletes priority-3 first, then priority-2 (oldest first).
 * Never touches priority-1 data.
 */
export function emergencyCleanup(): { freed: number; keysRemoved: string[] } {
  const meta = loadMeta();
  const removed: string[] = [];
  const startUsage = getStorageUsage();

  // Collect deletable keys with their priority and age
  const candidates: { key: string; priority: RetentionPriority; lastWritten: number }[] = [];
  for (const key of getAllMadinaKeys()) {
    const policy = getPolicyForKey(key);
    if (policy.priority === 1) continue; // Never delete critical data
    candidates.push({
      key,
      priority: policy.priority,
      lastWritten: meta[key] ?? 0,
    });
  }

  // Sort: priority 3 before 2, oldest first within each priority
  candidates.sort((a, b) => {
    if (a.priority !== b.priority) return b.priority - a.priority; // 3 first
    return a.lastWritten - b.lastWritten; // oldest first
  });

  // Delete until below 75% quota
  for (const { key } of candidates) {
    if (getQuotaPercent() <= 75) break;
    removeStorageItem(key);
    delete meta[key];
    removed.push(key);
  }

  if (removed.length > 0) saveMeta(meta);
  return { freed: startUsage - getStorageUsage(), keysRemoved: removed };
}

/**
 * Run on app startup: prune expired data and handle high quota.
 */
export function runStartupMaintenance(): void {
  if (typeof window === 'undefined') return;

  // Register quota exceeded handler
  onQuotaExceeded(() => emergencyCleanup());

  // Prune expired data
  const { pruned } = pruneExpiredData();
  if (pruned > 0) {
    console.info(`[StorageRetention] Pruned ${pruned} expired keys`);
  }

  // Check quota
  const quotaPercent = getQuotaPercent();
  if (quotaPercent > 90) {
    console.warn(`[StorageRetention] Quota at ${quotaPercent}% — running emergency cleanup`);
    const { keysRemoved } = emergencyCleanup();
    if (keysRemoved.length > 0) {
      console.info(`[StorageRetention] Emergency cleanup removed ${keysRemoved.length} keys`);
    }
  } else if (quotaPercent > 75) {
    console.warn(`[StorageRetention] Storage usage at ${quotaPercent}% — consider clearing old data`);
  }
}
